import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema, generateWebPageSchema } from '../../utils/seo';

const HomePage: React.FC = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);

  // SEO Configuration
  useSEO({
    title: '嘉原不動產 - 房仲契約書購買 | 不動產委託銷售契約書訂購 | 專業房仲契約書線上購買',
    description: '房仲專用不動產專任委託銷售契約書，格式清楚、符合內政部規範，適合實務交易使用，可立即出貨。提供房仲契約書購買、契約書訂購服務，線上訂購後立即出貨，30+專業範本符合內政部規範。',
    keywords: '房仲契約書購買,契約書訂購,房仲契約書線上購買,不動產契約書購買,房仲契約書,不動產委託銷售契約書,房仲專用契約書,房仲買賣契約書,專任委託合約,不動產銷售契約,不動產契約書範本,專售房仲公司專用契約書,專任委託跟一般委託差在哪,契約書範本,不動產契約書,不動產買賣契約書,不動產交易定型化契約範本,房屋買賣契約書,委託銷售契約書,不動產委託銷售契約書範本,快速成交',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '嘉原不動產 - 房仲契約書購買 | 不動產委託銷售契約書訂購',
          '房仲專用不動產專任委託銷售契約書，格式清楚、符合內政部規範，適合實務交易使用，可立即出貨。提供房仲契約書購買、契約書訂購服務。',
          '/'
        ),
        generateOrganizationSchema(
          '嘉原不動產',
          '房仲專用不動產專任委託銷售契約書，格式清楚、符合內政部規範，適合實務交易使用，可立即出貨。提供房仲契約書購買、契約書訂購服務，30+專業範本符合內政部規範，適合獨立品牌不動產仲介使用，訂購後立即出貨無浮水印版本，提升成交效率。',
          'https://static.readdy.ai/image/4e3cf408545f0c2efa1ef6dd2e77e8cf/9d6409c45135f50f090b60b201609ac4.png',
          '全台灣'
        )
      ]
    }
  });

  const categories = [
    {
      type: 'sales-commission',
      title: '銷售委託類',
      description: '賣方委託：屋主／地主委託仲介銷售',
      icon: 'ri-file-user-line',
      color: '#2d5016'
    },
    {
      type: 'lease-commission',
      title: '租賃委託與租賃契約類',
      description: '出租、承租、代管、包租、轉租',
      icon: 'ri-key-2-line',
      color: '#52c41a'
    },
    {
      type: 'purchase-contract',
      title: '買賣契約類',
      description: '正式買賣簽約',
      icon: 'ri-file-text-line',
      color: '#1a2332'
    },
    {
      type: 'property-disclosure',
      title: '標的現況／不動產說明文件',
      description: '揭露屋況、土地現況、交易透明',
      icon: 'ri-file-list-2-line',
      color: '#1890ff'
    },
    {
      type: 'buyer-documents',
      title: '買方／承購相關文件',
      description: '斡旋、議價、承購意向',
      icon: 'ri-user-received-line',
      color: '#722ed1'
    },
    {
      type: 'payment-documents',
      title: '收款、費用與報酬文件',
      description: '定金、服務費、報酬承諾',
      icon: 'ri-money-dollar-circle-line',
      color: '#fa8c16'
    },
    {
      type: 'registration-documents',
      title: '地政士／登記相關文件',
      description: '過戶、登記、地政士配合',
      icon: 'ri-government-line',
      color: '#13c2c2'
    },
    {
      type: 'other-documents',
      title: '其他',
      description: '補充、變更、特殊用途',
      icon: 'ri-folder-line',
      color: '#8c8c8c'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar onCartClick={() => setIsCartOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/faeb5443951554f9c9f9b1657674e7c2.jpg"
            alt="房仲契約書購買"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full py-12">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              房仲契約書購買<br />
              <span className="text-[#d4af37]">專業契約書線上訂購</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/85 mb-8 font-light leading-relaxed">
              30+ 專業範本，符合內政部規範<br />
              線上訂購，立即出貨使用
            </p>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-6 mb-12">
              <Link
                to="/contracts"
                className="px-8 sm:px-10 py-3 sm:py-4 bg-[#d4af37] text-[#1a2332] font-bold rounded-lg hover:bg-[#c49d2e] transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl text-center"
              >
                立即購買契約書
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <Link
                to="/contact"
                className="px-8 sm:px-10 py-3 sm:py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-[#1a2332] transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
              >
                了解訂購流程
              </Link>
            </div>

            {/* 簡潔的特色標籤 */}
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <div className="flex items-center gap-2 text-white/90">
                <i className="ri-checkbox-circle-fill text-[#d4af37]"></i>
                <span className="text-sm sm:text-base">內政部規範</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <i className="ri-download-cloud-fill text-[#d4af37]"></i>
                <span className="text-sm sm:text-base">立即出貨</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <i className="ri-file-text-fill text-[#d4af37]"></i>
                <span className="text-sm sm:text-base">無浮水印</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">契約書分類</h2>
            <p className="text-base sm:text-lg text-gray-600">選擇您需要的契約類型，線上訂購後立即出貨</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {categories.map((category) => (
              <Link
                key={category.type}
                to={`/contracts?category=${category.type}`}
                className="group bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#d4af37]"
              >
                <div 
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 transition-all duration-300"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <i 
                    className={`${category.icon} text-3xl sm:text-4xl`}
                    style={{ color: category.color }}
                  ></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#d4af37] transition-colors duration-300">
                  {category.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">
                  {category.description}
                </p>
                <div className="flex items-center text-[#d4af37] font-semibold group-hover:gap-3 transition-all duration-300 text-sm sm:text-base">
                  查看範本
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform duration-300"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-[#1a2332] to-[#2d3e50] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">為什麼選擇我們？</h2>
              <p className="text-lg sm:text-xl text-white/80 mb-8 sm:mb-12 leading-relaxed">
                專業、完整的房仲契約書服務，讓您的交易更加順利安全
              </p>

              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#d4af37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-xl sm:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">符合內政部規範</h3>
                    <p className="text-sm sm:text-base text-white/70">所有範本符合內政部應記載及不應記載事項規範，適合獨立品牌不動產仲介使用</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#d4af37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-cart-line text-xl sm:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">線上訂購立即出貨</h3>
                    <p className="text-sm sm:text-base text-white/70">便利的線上訂購流程，完成購買後立即取得無浮水印版本</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#d4af37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-xl sm:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">完善客戶服務</h3>
                    <p className="text-sm sm:text-base text-white/70">提供專業的售後服務，解答您在購買與使用過程中的任何疑問</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  <div className="text-center p-4 sm:p-6 bg-white/5 rounded-xl">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#d4af37] mb-2">30+</div>
                    <div className="text-sm sm:text-base text-white/70">契約範本</div>
                  </div>
                  <div className="text-center p-4 sm:p-6 bg-white/5 rounded-xl">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#d4af37] mb-2">500+</div>
                    <div className="text-sm sm:text-base text-white/70">滿意客戶</div>
                  </div>
                  <div className="text-center p-4 sm:p-6 bg-white/5 rounded-xl">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#d4af37] mb-2">100%</div>
                    <div className="text-sm sm:text-base text-white/70">法規符合</div>
                  </div>
                  <div className="text-center p-4 sm:p-6 bg-white/5 rounded-xl">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#d4af37] mb-2">24/7</div>
                    <div className="text-sm sm:text-base text-white/70">線上服務</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">開始購買房仲契約書</h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 sm:mb-12">
            瀏覽契約書範本，訂購後立即出貨
          </p>
          <Link
            to="/contracts"
            className="inline-flex items-center gap-3 px-10 sm:px-12 py-4 sm:py-5 bg-[#d4af37] text-[#1a2332] font-bold text-base sm:text-lg rounded-lg hover:bg-[#c49d2e] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            立即訂購契約書
            <i className="ri-arrow-right-line text-xl sm:text-2xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
